import Image from "next/image";
import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import {
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  Skeleton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Link from "next/link";
import { useDispatch, useSelector } from "react-redux";
import { getActivitiesListServices } from "../../redux/slices/common";

const CategorySlider = ({ router, pageName, title = "Categories" }) => {
  const dispatch = useDispatch();
  const [isCategoryNotAvailable, setIsCategoryNotAvailable] = useState(false);
  const [services, setServices] = useState([]);
  const { activityList } = useSelector(state => state.common)
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    const isActivityPage = pageName === "activities";
    if (isActivityPage) {
      setLoader(true);
      dispatch(getActivitiesListServices());
    }
    setIsCategoryNotAvailable(!isActivityPage);
  }, [router.asPath]);


  // console.log("services", services);

  useEffect(() => {
    if (activityList && activityList !== undefined) {
      setLoader(false);
      setServices(activityList?.servicesList);
    }
  }, [activityList]);

  if (isCategoryNotAvailable) return null;

  const navBtnSx = (pos) => ({
    position: "absolute",
    top: "35%",
    [pos]: { xs: -4, sm: -18, md: -36 },
    zIndex: 10,
    color: "white",
    background: "linear-gradient(135deg, #75C4C5, #0D8196)",
    // border: "2px solid #0d8196",
    "&:hover": {
      background: "#0d8196",
      color: "white",
    },
    display: { xs: "none", md: "flex" },
  });

  return (
    <Box sx={{ position: "relative", mx: 'auto' }}>
      {/* Carousel Container */}
      <Box
        sx={{
          position: "relative",
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 0, sm: 2, md: 0 }
        }}
      >
        {/* Prev/Next Buttons (visible ≥ md) */}
        {services?.length >= 3 && (
          <>
            <IconButton
              className="activity-prev"
              aria-label="prev"
              sx={{
                ...navBtnSx("left"),
                left: "-14px",
                display: "flex",
                zIndex: 10, // ✅ ensure visible above slides
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              className="activity-next"
              aria-label="next"
              sx={{
                ...navBtnSx("right"),
                // right: "2px",
                right: "-10px",
                display: "flex",
                zIndex: 10, // ✅ ensure visible above slides
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
          </>
        )}

        {/* Swiper Carousel */}
        <Swiper
          modules={[Navigation, Pagination]}
          navigation={
            services?.length >= 3
              ? { prevEl: ".activity-prev", nextEl: ".activity-next" }
              : false
          }
          pagination={{
            el: ".activity-dots",
            clickable: true,
            renderBullet: (index, className) => `<span class="${className}"></span>`,
          }}
          slidesPerView={"auto"}
          spaceBetween={20}
          slidesOffsetBefore={0}
          slidesOffsetAfter={0}
          style={{
            paddingBottom: 40,
            paddingLeft: 10,
            paddingTop: 10,
            paddingRight: 10,
            margin: "0 auto",
            maxWidth: '100vw',
            overflow: "hidden",
          }}
        >
          {/* ✅ Show skeletons when loading */}
          {loader
            ? Array.from({ length: 3 }).map((_, i) => (
              <SwiperSlide key={i} style={{ width: 380, position: "relative" }}>
                <Box
                  sx={{
                    borderRadius: "40px",
                    overflow: "hidden",
                    border: "4px solid transparent",
                    background:
                      "linear-gradient(white, white) padding-box, linear-gradient(135deg, #75C4C5, #0D8196) border-box",
                    boxShadow:
                      "0 6px 20px rgba(0,0,0,0.08), 0 3px 10px rgba(0,0,0,0.05)",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={200}
                    sx={{ borderRadius: "36px 36px 0 0" }}
                  />
                  <Box sx={{ p: 2 }}>
                    <Skeleton variant="text" width="80%" height={28} />
                    <Stack direction="row" spacing={1} mt={1}>
                      <Skeleton variant="rounded" width={70} height={24} />
                      <Skeleton variant="rounded" width={70} height={24} />
                      <Skeleton variant="rounded" width={100} height={24} />
                    </Stack>
                  </Box>
                </Box>
              </SwiperSlide>
            ))
            : services?.map((category, i) => (
              <SwiperSlide
                key={i}
                style={{
                  width: 380,
                  position: "relative",
                  overflow: "visible", // allow card to grow on hover
                }}
              >
                <Link
                  href={`/activities/activities?type=${category?.name}`}
                  target="_blank"
                  style={{ textDecoration: "none" }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: "40px",
                      overflow: "hidden",
                      background:
                        "linear-gradient(white, white) padding-box, linear-gradient(135deg, #75C4C5, #0D8196) border-box",
                      border: "4px solid transparent",
                      // boxShadow:
                      //   "0 6px 20px rgba(0,0,0,0.08), 0 3px 10px rgba(0,0,0,0.05)",
                      transition: "transform 0.35s ease, box-shadow 0.35s ease",
                      transformOrigin: "center center",
                      transform: "scale(1)",
                      "&:hover": {
                        transform: "scale(1.05)",          // ✅ zoom in
                        // boxShadow: "0 12px 30px rgba(0,0,0,0.22)",
                      },
                    }}
                  >
                    <Image
                      width={380}
                      height={150}
                      alt={category.name}
                      src={`${process.env.IMAGE_BASE_URL}/${category?.image}`}
                      onError={(e) => (e.currentTarget.src = "/fallback-image.jpg")}
                      style={{
                        objectFit: "cover",
                        height: 230,
                      }}
                    />

                    {/* overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.05) 85%)",
                      }}
                    />

                    {/* top-right arrow */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255, 255, 255, 0.35)",
                        border: "2px solid #ffffff",
                        backdropFilter: "blur(1px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bosxShadow: "0 2px 5px rgba(0,0,0,0.25)",
                        zIndex: 6,
                      }}
                    >
                      <Image
                        src="/img/header/topRightBlack.svg"
                        alt="Open"
                        width={16}
                        height={16}
                        style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.3))" }}
                      />
                    </Box>

                    {/* text content */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 2,
                        background:
                          "linear-gradient(to top, rgba(0, 0, 0, 0.75) 40%, rgba(0, 0, 0, 0) 80%)",
                        color: "#fff",
                        textAlign: "start",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "#fff",
                          fontSize: 25,
                          mb: 0.8,
                        }}
                      >
                        {category.name}
                      </Typography>

                      <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          flexWrap: "nowrap",
                          overflow: "hidden",
                          maxWidth: "100%",
                        }}
                      >
                        {["Secure", "Safe", "Pick N Drop"].map((text, i) => (
                          <Box
                            key={i}
                            sx={{
                              fontSize: 13,
                              px: 1.8,
                              py: 0.3,
                              borderRadius: "20px",
                              border: "1.5px solid #75C4C5",
                              background:
                                "linear-gradient(180deg, rgba(117,196,197,0.3) 0%, rgba(13,129,150,0.5) 100%)",
                              color: "#fff",
                              fontWeight: 500,
                              letterSpacing: 0.3,
                              boxShadow: "inset 0 1px 2px rgba(255,255,255,0.25)",
                              backdropFilter: "blur(4px)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {text}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                </Link>
              </SwiperSlide>
            ))}
        </Swiper>

      </Box>
    </Box>
  );
};

export default CategorySlider;
